<script setup>
import { pushEvent } from '#common/utils/user'

const props = defineProps({
  id: { type: String, required: true },
  startOpen: { type: Boolean, default: true },
})

const { $auth, $store } = useNuxtApp()
const route = useRoute()
const emit = defineEmits(['registration-success'])
const { isLoading } = storeToRefs(useLayout())

const formValues = ref({
  first_name: '',
  last_name: '',
  email: '',
  date_of_birth: '',
  password: '',
})
const formErrors = ref([])
const drawerOpen = ref(props.startOpen)
const formName = 'epRegistrationForm'
const displayEmailExistsError = ref(false)
const firstCompletion = ref(true)
const needRecaptcha = ref(!$auth.loggedIn)
const recaptchaComponent = ref(null)

const committedName = computed(() => {
  return $auth.loggedIn
    ? `${$auth.user.first_name} ${$auth.user.last_name}`
    : ''
})

const committedEmail = computed(() => {
  return $auth.loggedIn ? $auth.user.email : ''
})

const commitedDOB = computed(() => {
  return $auth.loggedIn
    ? moment($auth.user.date_of_birth, 'YYYY-MM-DD').format('MM/DD/YYYY')
    : ''
})

const sectionClasses = computed(() => {
  const classes = ['checkout-details-section']
  if (!drawerOpen.value) classes.push('edit-section')
  return classes
})

onMounted(() => {
  if ($auth.loggedIn) {
    formValues.value = {
      firstName: $auth.user.first_name,
      lastName: $auth.user.last_name,
      email: $auth.user.email,
      dob: $auth.user.date_of_birth,
      password: '',
    }
  }
})

const commit = async () => {
  clearFormErrors()

  const registrationData = {
    email: formValues.value.email,
    first_name: formValues.value.first_name,
    last_name: formValues.value.last_name,
    password: formValues.value.password,
    date_of_birth: formValues.value.date_of_birth,
    default_zipcode: $auth.user.zipcode,
    is_club: false,
    is_futures: true,
    referrer: route.fullPath,
  }

  const recaptchaResponse = recaptchaComponent.value?.getRecaptchaResponse()
  if (recaptchaResponse && needRecaptcha.value) {
    registrationData.recaptcha = recaptchaResponse
  }
  isLoading.value = true
  try {
    await $auth.registerUser(registrationData)
    $auth.authEmail = registrationData.email?.trim() || null
    if (firstCompletion.value) {
      pushEvent(
        {
          metric_name: 'Checkout Account Details Complete',
          url_path: route.fullPath,
        },
        $store,
      )
      firstCompletion.value = false
    }
    if (recaptchaResponse) {
      recaptchaComponent.value.resetRecaptcha()
    }
    if (!$auth.loggedIn) {
      emit('registration-success', {
        verificationEmail: $auth.authEmail,
      })
    }
  } catch (e) {
    recaptchaComponent.value?.resetRecaptcha()
    const errorPayload = e.errors
    if (errorPayload) {
      if (errorPayload.email?.[0]?.includes('already exists')) {
        displayEmailExistsError.value = true
        delete errorPayload.email
      }
      for (const errorKey in errorPayload) {
        const errorValue = errorPayload[errorKey]
        if (Array.isArray(errorValue)) {
          formErrors.value.push(...errorValue)
        }
      }
    } else {
      Sentry.captureException(e)
    }
  } finally {
    isLoading.value = false
  }
}
const closeDrawer = () => {
  formErrors.value = []
  drawerOpen.value = false
}

const clearFormErrors = () => {
  formErrors.value = []
  displayEmailExistsError.value = false
}

const emailExistsLoginModal = () => {
  $auth.openAuthModal({ step: 'login', email: formValues.value.email })
}
</script>
<template>
  <CheckoutSection
    :id="id"
    section-title=""
    :show-section-number="false"
    :section-number="1"
    :enabled="true"
    :drawer-open="drawerOpen"
    :show-change-link="false"
    :classes="sectionClasses"
    @drawerClosed="closeDrawer"
  >
    <template #collapsed>
      <div class="checkout-section-summary">
        <ul>
          <li>{{ committedName }}</li>
          <li>{{ committedEmail }}</li>
          <li>{{ commitedDOB }}</li>
        </ul>
      </div>
    </template>
    <template #expanded>
      <div class="checkout-section-content">
        <FormKit
          id="ep-account-information"
          v-model="formValues"
          type="form"
          :actions="false"
          :name="formName"
          class="checkout-form"
          @submit="commit"
        >
          <div class="row">
            <div class="columns medium-6">
              <div class="floating">
                <FloatingInputFK
                  id="input-buyer-firstname"
                  name="first_name"
                  validation="required"
                  placeholder="First Name"
                  label="First Name"
                />
              </div>
            </div>
            <div class="columns medium-6">
              <div class="floating">
                <FloatingInputFK
                  id="input-buyer-lastname"
                  name="last_name"
                  validation="required"
                  placeholder="Last Name"
                  label="Last Name"
                />
              </div>
            </div>
          </div>
          <div class="row">
            <div class="columns medium-6">
              <div class="floating">
                <FloatingInputFK
                  id="input-buyer-email"
                  name="email"
                  validation="bail|required|email"
                  placeholder="Email Address"
                  label="Email Address"
                />
              </div>
            </div>
          </div>
          <div class="row">
            <div class="columns medium-6">
              <div class="floating">
                <label for="input-dob" class="static__label">
                  <strong> Date of Birth </strong>
                  (Only used to verify you're age 21 or older)
                </label>
                <DateFormattedTextFieldFK
                  id="input-dob"
                  name="date_of_birth"
                  input-class="static__input"
                  date-placeholder="MM/DD/YYYY"
                  validation="bail|required|date:YYYY-MM-DD|livingBirthday|legalBirthday"
                  :max="moment().format('YYYY-MM-DD')"
                />
              </div>
            </div>
          </div>
          <div class="row">
            <div class="columns medium-6">
              <div class="floating">
                <FloatingInputFK
                  id="password"
                  name="password"
                  placeholder="Password"
                  label="Password"
                  type="password"
                  validation="^required|length:12|matches:/[0-9]/"
                  validation-visibility="dirty"
                  :validation-messages="{
                    matches: 'Error: Passwords require at least one number.',
                  }"
                />
              </div>
            </div>
            <div class="columns medium-6">
              <div class="floating">
                <FloatingInputFK
                  id="password-confirm"
                  name="password_confirm"
                  validation="required|confirm"
                  placeholder="Confirm Password"
                  label="Confirm Password"
                  type="password"
                  validation-visibility="dirty"
                  :validation-messages="{
                    confirm: 'Error: Passwords must match.',
                  }"
                />
              </div>
            </div>
          </div>
          <Recaptcha v-if="needRecaptcha" ref="recaptchaComponent" />
          <div class="columns">
            <ul v-if="displayEmailExistsError" class="errorlist">
              <li>
                Error: A member with this email already exists.
                <a href="#" @click.prevent="emailExistsLoginModal">
                  Click here to login or to reset your password.
                </a>
              </li>
            </ul>
            <ErrorList :errors="formErrors" />
          </div>
          <div class="row">
            <div class="columns small-12 medium-9 large-6">
              <FormKit
                input-class="button primary"
                type="submit"
                style="margin: 0 0 1.25rem"
              >
                Continue
              </FormKit>
            </div>
          </div>
        </FormKit>
      </div>
    </template>
  </CheckoutSection>
</template>
